import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import {
  Smartphone, Laptop, Headphones, Watch, Tv, Gamepad2, Cable,
  Truck, ShieldCheck, BadgeIndianRupee, Wrench, RefreshCcw,
  ArrowRight, Clock, ShoppingBag, Sparkles
} from "lucide-react";

const CATEGORIES = [
  { id: "phones", label: "Phones", icon: Smartphone, color: "#DCE7FB" },
  { id: "laptops", label: "Laptops", icon: Laptop, color: "#CFE6FB" },
  { id: "audio", label: "Audio", icon: Headphones, color: "#E2DEFB" },
  { id: "wearables", label: "Wearables", icon: Watch, color: "#D4ECFB" },
  { id: "tv", label: "TVs", icon: Tv, color: "#E8E1FB" },
  { id: "gaming", label: "Gaming", icon: Gamepad2, color: "#D9E5FE" },
  { id: "accessories", label: "Accessories", icon: Cable, color: "#CCE8FB" },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/products/featured").then(({ data }) => setFeatured(data.slice(0, 8))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page" className="bg-white">
      {/* HERO */}
      <section className="vm-hero-gradient">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 py-10 md:py-14">
          <div className="grid lg:grid-cols-12 gap-6 items-center">
            <div className="lg:col-span-7 vm-stagger">
              <span className="vm-delivery-pill">
                <Clock className="h-3.5 w-3.5" /> Delivery in 30 minutes
              </span>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold mt-4 leading-[1.05] tracking-tight text-[#0F1115]">
                Electronics, delivered<br />
                in <span className="text-grass-600">30 minutes</span>.
              </h1>
              <p className="text-gray-600 text-base md:text-lg mt-3 max-w-xl leading-relaxed">
                Phones, laptops, audio, TVs & more — at the best prices. Book repairs and sell old devices in a tap.
              </p>

              {/* Three core flows — Shop / Service / Sell device */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5 max-w-2xl">
                <Link
                  to="/shop"
                  className="vm-card p-4 flex items-center gap-3 group hover:!border-volt-500"
                  data-testid="hero-flow-shop"
                >
                  <div className="h-11 w-11 rounded-xl bg-volt-500/10 flex items-center justify-center shrink-0 group-hover:bg-volt-500 transition-colors">
                    <ShoppingBag className="h-5 w-5 text-volt-500 group-hover:text-white transition-colors" />
                  </div>
                  <div className="leading-tight min-w-0">
                    <p className="text-sm font-bold text-gray-900">Shop</p>
                    <p className="text-xs text-gray-500 truncate">Buy electronics</p>
                  </div>
                  <ArrowRight className="h-4 w-4 text-gray-400 ml-auto group-hover:text-volt-500 group-hover:translate-x-0.5 transition-all" />
                </Link>
                <Link
                  to="/services"
                  className="vm-card p-4 flex items-center gap-3 group hover:!border-volt-500"
                  data-testid="hero-flow-service"
                >
                  <div className="h-11 w-11 rounded-xl bg-volt-500/10 flex items-center justify-center shrink-0 group-hover:bg-volt-500 transition-colors">
                    <Wrench className="h-5 w-5 text-volt-500 group-hover:text-white transition-colors" />
                  </div>
                  <div className="leading-tight min-w-0">
                    <p className="text-sm font-bold text-gray-900">Service</p>
                    <p className="text-xs text-gray-500 truncate">Repair & install</p>
                  </div>
                  <ArrowRight className="h-4 w-4 text-gray-400 ml-auto group-hover:text-volt-500 group-hover:translate-x-0.5 transition-all" />
                </Link>
                <Link
                  to="/buyback"
                  className="vm-card p-4 flex items-center gap-3 group hover:!border-volt-500"
                  data-testid="hero-flow-sell"
                >
                  <div className="h-11 w-11 rounded-xl bg-volt-500/10 flex items-center justify-center shrink-0 group-hover:bg-volt-500 transition-colors">
                    <RefreshCcw className="h-5 w-5 text-volt-500 group-hover:text-white transition-colors" />
                  </div>
                  <div className="leading-tight min-w-0">
                    <p className="text-sm font-bold text-gray-900">Sell old device</p>
                    <p className="text-xs text-gray-500 truncate">Instant cash</p>
                  </div>
                  <ArrowRight className="h-4 w-4 text-gray-400 ml-auto group-hover:text-volt-500 group-hover:translate-x-0.5 transition-all" />
                </Link>
              </div>

              <div className="flex flex-wrap gap-x-5 gap-y-2 mt-6 text-sm text-gray-700">
                <span className="flex items-center gap-1.5"><Truck className="h-4 w-4 text-grass-600" /> Free delivery over ₹999</span>
                <span className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-grass-600" /> 100% authentic</span>
                <span className="flex items-center gap-1.5"><BadgeIndianRupee className="h-4 w-4 text-grass-600" /> COD available</span>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="relative grid grid-cols-2 gap-3">
                <CategoryTile cat={CATEGORIES[0]} large />
                <div className="grid gap-3">
                  <CategoryTile cat={CATEGORIES[1]} />
                  <CategoryTile cat={CATEGORIES[2]} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-4 lg:px-6 py-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight">Shop by category</h2>
          <Link to="/shop" className="text-sm text-grass-700 font-semibold hover:text-grass-600 flex items-center gap-1" data-testid="see-all-categories">
            See all <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-3">
          {CATEGORIES.map((c) => (
            <Link
              key={c.id}
              to={`/shop?category=${c.id}`}
              className="vm-card p-3 flex flex-col items-center gap-2 text-center"
              data-testid={`category-${c.id}`}
            >
              <div
                className="h-14 w-14 rounded-2xl flex items-center justify-center"
                style={{ backgroundColor: c.color }}
              >
                <c.icon className="h-6 w-6 text-[#0F1115]" />
              </div>
              <span className="text-xs sm:text-sm font-semibold text-[#0F1115]">{c.label}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* TWO PROMO TILES — Services + Buyback */}
      <section className="max-w-7xl mx-auto px-4 lg:px-6 py-6">
        <div className="grid md:grid-cols-2 gap-4">
          <Link
            to="/buyback"
            className="relative vm-card p-6 md:p-7 overflow-hidden bg-[#EAF1FC] hover:translate-y-[-2px]"
            data-testid="promo-buyback"
          >
            <span className="vm-chip vm-chip-accent">Instant cash</span>
            <h3 className="font-display text-2xl md:text-3xl font-bold mt-3 leading-tight text-[#0B1220]">
              Sell your old<br />phone or laptop
            </h3>
            <p className="text-sm text-gray-700 mt-2 max-w-xs">
              Get a quote in 30 seconds. Free doorstep pickup, instant payment.
            </p>
            <span className="vm-btn-primary inline-flex items-center gap-2 mt-5 text-sm">
              Get my quote <ArrowRight className="h-4 w-4" />
            </span>
            <Sparkles className="absolute right-4 bottom-4 h-24 w-24 text-[#C4D5F8]" />
          </Link>

          <Link
            to="/services"
            className="relative vm-card p-6 md:p-7 overflow-hidden bg-[#E6F4FE] hover:translate-y-[-2px]"
            data-testid="promo-services"
          >
            <span className="vm-chip vm-chip-primary">Same-day visit</span>
            <h3 className="font-display text-2xl md:text-3xl font-bold mt-3 leading-tight text-[#0B1220]">
              Repair & install<br />at your doorstep
            </h3>
            <p className="text-sm text-gray-700 mt-2 max-w-xs">
              Certified technicians for phones, laptops, TVs and more.
            </p>
            <span className="vm-btn-primary inline-flex items-center gap-2 mt-5 text-sm">
              Book a service <Wrench className="h-4 w-4" />
            </span>
            <RefreshCcw className="absolute right-4 bottom-4 h-24 w-24 text-[#B6DDF8]" />
          </Link>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="max-w-7xl mx-auto px-4 lg:px-6 py-8">
        <div className="flex items-end justify-between mb-4">
          <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight">Trending now</h2>
          <Link to="/shop" className="text-sm text-grass-700 font-semibold hover:text-grass-600 flex items-center gap-1">
            See all <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
          {featured.map((p) => (
            <ProductCard key={p.product_id} product={p} />
          ))}
        </div>
      </section>

      {/* WHY US */}
      <section className="max-w-7xl mx-auto px-4 lg:px-6 py-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { icon: Clock, t: "30-min delivery", d: "In your city" },
            { icon: ShieldCheck, t: "100% authentic", d: "Brand warranty" },
            { icon: Truck, t: "Free pickup", d: "For buybacks" },
            { icon: BadgeIndianRupee, t: "Best prices", d: "No-cost EMI" },
          ].map((b, i) => (
            <div key={i} className="vm-card p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-grass-50 flex items-center justify-center shrink-0">
                <b.icon className="h-5 w-5 text-grass-600" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[#0F1115] truncate">{b.t}</p>
                <p className="text-xs text-gray-500 truncate">{b.d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function CategoryTile({ cat, large }) {
  return (
    <Link
      to={`/shop?category=${cat.id}`}
      className={`rounded-2xl p-4 flex flex-col justify-between hover:shadow-soft transition-shadow ${large ? "min-h-[200px]" : "min-h-[95px]"}`}
      style={{ backgroundColor: cat.color }}
      data-testid={`hero-tile-${cat.id}`}
    >
      <p className="text-xs font-bold uppercase tracking-wider text-[#0F1115]/70">{cat.label}</p>
      <div className="flex items-end justify-between">
        <p className={`font-display font-extrabold text-[#0F1115] leading-none ${large ? "text-3xl" : "text-xl"}`}>
          Shop now
        </p>
        <cat.icon className={`text-[#0F1115] ${large ? "h-12 w-12" : "h-7 w-7"}`} strokeWidth={1.8} />
      </div>
    </Link>
  );
}
